// Radha — Account Aggregator consent management, surfaced inside
// Reconciliation. Request a consent, see its status, and pull transactions
// into the same bank_transactions table Reconciliation already reads —
// there's no separate "AA data" screen, the feed just lands where
// reconciliation already happens.
import React, { useState } from "react";

import {
  useAaConsents, useCreateAaConsent, useAaFetchData, useAaFetchSessions, aaErrorMessage,
} from "../hooks/useAccountAggregator";

const STATUS_COLOR: Record<string, string> = {
  active: "var(--status-good)", requested: "var(--fg-muted)", pending: "var(--status-warning)",
  paused: "var(--status-warning)", revoked: "var(--status-critical)",
  rejected: "var(--status-critical)", expired: "var(--fg-muted)",
};

const todayPlus = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

function RequestConsentForm({ canWrite }: { canWrite: boolean }) {
  const create = useCreateAaConsent();
  const [label, setLabel] = useState("");
  const [mobile, setMobile] = useState("");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  if (!canWrite) return null;

  const submit = () => {
    setNote(null);
    create.mutate(
      { subject_label: label, customer_mobile: mobile, date_from: todayPlus(-90), date_to: todayPlus(0) },
      {
        onSuccess: () => { setNote({ ok: true, text: "Consent requested — the customer approves it in their AA app" }); setLabel(""); setMobile(""); },
        onError: (e) => setNote({ ok: false, text: aaErrorMessage(e) }),
      },
    );
  };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, background: "var(--surface-2)" }}>
      <div style={{ font: "600 13px Inter", marginBottom: 8 }}>Request bank-feed consent</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Account label (e.g. HDFC current a/c)"
          style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)", minWidth: 220 }} />
        <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="AA-linked mobile (10 digits)"
          style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)", width: 180 }} />
        <button onClick={submit} disabled={create.isPending || !label || mobile.length !== 10}
          style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13, opacity: create.isPending || !label || mobile.length !== 10 ? 0.5 : 1 }}>
          {create.isPending ? "Requesting…" : "Request consent"}
        </button>
      </div>
      {note && <p style={{ fontSize: 13, margin: "8px 0 0", color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</p>}
    </div>
  );
}

function ConsentRow({ consent, canWrite }: { consent: import("../hooks/useAccountAggregator").AaConsent; canWrite: boolean }) {
  const fetchData = useAaFetchData();
  const [note, setNote] = useState<string | null>(null);
  const sync = () => {
    setNote(null);
    fetchData.mutate(consent.id, {
      onSuccess: (r) => setNote(`Imported ${r.imported} transaction${r.imported === 1 ? "" : "s"}`),
      onError: (e) => setNote(aaErrorMessage(e)),
    });
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: "1px solid var(--border)", fontSize: 13 }}>
      <span style={{ flex: 1 }}>{consent.subject_label}</span>
      <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>{consent.customer_mobile}</span>
      <span style={{ textTransform: "capitalize", color: STATUS_COLOR[consent.status] ?? "var(--fg)", fontWeight: 600, minWidth: 74 }}>{consent.status}</span>
      {consent.status === "active" && canWrite && (
        <button onClick={sync} disabled={fetchData.isPending}
          style={{ padding: "3px 10px", fontSize: 12, border: "1px solid var(--accent-purple)", color: "var(--accent-purple)", background: "none", borderRadius: 6, cursor: "pointer" }}>
          {fetchData.isPending ? "Syncing…" : "Sync now"}
        </button>
      )}
      {note && <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>{note}</span>}
    </div>
  );
}

export function BankConnectivity({ canWrite }: { canWrite: boolean }) {
  const { data: consents, isLoading } = useAaConsents();
  useAaFetchSessions(); // kept warm for the sync-history the consent rows reference indirectly

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={{ font: "600 15px Inter" }}>Bank connectivity (Account Aggregator)</div>
        <span style={{ fontSize: 11.5, color: "var(--fg-muted)" }}>Auto-pulls bank credits here — no manual entry</span>
      </div>
      <RequestConsentForm canWrite={canWrite} />
      {isLoading ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>Loading…</p>
      ) : (consents ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No bank-feed consents yet.</p>
      ) : (
        <div>{(consents ?? []).map((c) => <ConsentRow key={c.id} consent={c} canWrite={canWrite} />)}</div>
      )}
    </div>
  );
}
